// Client list reports — pre-built queries the agent can run against the
// backend ("birthdays this month", "renewals next 60 days", "seniors", etc).
// Designed as the simple counterpart to ReportsAnalyticsView: this one
// produces actionable LISTS of clients rather than charts/KPIs.
//
// Every run hits the backend (no client-side data is filtered), so the
// agent always sees every matching client regardless of pagination. The
// backend also writes an audit log entry per execution (HIPAA §164.312(b)).

import { useMemo, useState } from 'react';
import { COLUMNAS_DE_DEPENDIENTES, etiquetaDeDependiente, etiquetaDeEstatusDeDependiente } from '../columnasDeDependientes.js';
import {
  Cake, CalendarClock, UserPlus, Users, Shield, MapPin,
  FileX2, Tag, Building2, Sparkles, Download, ArrowLeft, X,
  LayoutGrid, Wrench,
} from 'lucide-react';
import { runClientReport, crearHojaDesdeInforme } from '../api.js';
import { fmt } from '../utils.js';
import { toast } from './Toast.jsx';
import { POLICY_TYPES } from '../policyTypes.js';
import { totalesDePersonas } from '../totalesDePersonas.js';
import { CustomReportsTab } from './CustomReportsTab.jsx';
import { GroupedChart } from './teamReportChart.jsx';
import { etiquetaDeEstatus } from '../estatusMigratorio.js';

// What a client list can be grouped by. Only plain, low-cardinality fields:
// grouping by name or email would draw one bar per person, which is a table
// with extra steps. No PHI beyond what the table already shows.
const CLIENT_DIMENSIONS = (es) => [
  { key: 'status', label: es ? 'Estado' : 'Status', get: (r) => r.status },
  { key: 'state', label: es ? 'Estado (US)' : 'State', get: (r) => r.state },
  { key: 'city', label: es ? 'Ciudad' : 'City', get: (r) => r.city },
  { key: 'source', label: es ? 'Fuente' : 'Source', get: (r) => r.source },
  { key: 'agent', label: es ? 'Agente' : 'Agent', get: (r) => r.assignedAgent || r.agentName },
  { key: 'language', label: es ? 'Idioma' : 'Language', get: (r) => r.preferredLanguage },
  // timeline: se ordena por fecha, no por tamaño — ver ReportChart.preserveOrder.
  { key: 'createdMonth', label: es ? 'Mes de alta' : 'Created month', get: (r) => clientMonthKey(r.createdAt), timeline: true },
];

// "YYYY-MM" from LOCAL date parts — toISOString() would shift day-1 rows into
// the previous month in Florida.
function clientMonthKey(v) {
  if (!v) return '';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? new Date(`${v}T00:00:00`) : new Date(v);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// "YYYY-MM-DD" -> "MM/DD/YYYY", SIN pasar por `new Date`.
//
// `new Date('2026-10-05')` se interpreta como medianoche UTC, y en Florida eso
// se pinta como el 4 de octubre: un vencimiento movido un día hacia atrás en
// una lista que existe justamente para llamar a tiempo. Partir la cadena no
// tiene ese problema porque no hay ninguna zona horaria de por medio.
function fechaCortaUS(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (v || '');
}

// US states (postal abbrev → name). Used by the by-state dropdown so the
// agent doesn't have to remember the two-letter codes.
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ['DC','District of Columbia'],['PR','Puerto Rico'],
];

// Static report catalog. Adding a new report = drop an entry here + a case
// in the backend switch. The catalog mirrors the backend reportIds 1:1.
function makeReportCatalog(lang) {
  const es = lang === 'es';
  return [
    {
      id: 'birthdays-this-month',
      title: es ? 'Cumpleaños este mes' : 'Birthdays this month',
      description: es
        ? 'Clientes que cumplen años en el mes en curso. Lista para tu campaña mensual de saludos.'
        : 'Clients with a birthday in the current month. Ready for your monthly outreach.',
      icon: Cake,
      color: '#ec4899',
      params: [],
    },
    {
      id: 'upcoming-renewals',
      title: es ? 'Renovaciones próximas' : 'Upcoming renewals',
      description: es
        ? 'Pólizas activas que vencen en los próximos N días. Ideal para empezar el seguimiento.'
        : 'Active policies expiring in the next N days. Start renewal outreach early.',
      icon: CalendarClock,
      color: '#0891b2',
      params: [{ name: 'days', label: es ? 'Días' : 'Days', type: 'number', default: 60 }],
    },
    {
      id: 'recent-clients',
      title: es ? 'Clientes nuevos' : 'Recent clients',
      description: es
        ? 'Clientes agregados en los últimos N días. Útil para medir tu velocidad de adquisición.'
        : 'Clients added in the last N days. Track your intake velocity.',
      icon: UserPlus,
      color: '#10b981',
      params: [{ name: 'days', label: es ? 'Días' : 'Days', type: 'number', default: 30 }],
    },
    {
      id: 'adults',
      title: es ? 'Mayores de edad (18+)' : 'Adults (18+)',
      description: es
        ? 'Clientes con 18 años o más. Incluye también los que no tienen fecha de nacimiento.'
        : 'Clients aged 18 or older. Also includes those without a date of birth.',
      icon: Users,
      color: '#3b82f6',
      params: [],
    },
    {
      id: 'seniors',
      title: es ? 'Adultos mayores' : 'Seniors',
      description: es
        ? 'Clientes mayores de cierta edad. Audiencia principal para Medicare.'
        : 'Clients above a given age. Primary Medicare audience.',
      icon: Shield,
      color: '#8b5cf6',
      params: [{ name: 'minAge', label: es ? 'Edad mínima' : 'Min age', type: 'number', default: 65 }],
    },
    {
      id: 'by-state',
      title: es ? 'Por estado' : 'By state',
      description: es
        ? 'Clientes que viven en un estado específico de EE.UU.'
        : 'Clients residing in a specific US state.',
      icon: MapPin,
      color: '#f59e0b',
      params: [{ name: 'state', label: es ? 'Estado' : 'State', type: 'state', default: 'FL' }],
    },
    {
      id: 'by-policy-type',
      title: es ? 'Por tipo de póliza' : 'By policy type',
      description: es
        ? 'Clientes con una póliza del tipo elegido, en cualquier estado (incluye canceladas). Estado = el del CLIENTE; Estado de póliza = el de la póliza viva si la tiene, y si no la última que tuvo.'
        : 'Clients with a policy of the chosen type, in any state (cancelled included). Status = the CLIENT\'s; Policy status = that of the live policy if there is one, otherwise the most recent one.',
      icon: Tag,
      color: '#0284c7',
      params: [{ name: 'type', label: es ? 'Tipo' : 'Type', type: 'policy-type', default: 'auto' }],
    },
    {
      id: 'by-carrier',
      title: es ? 'Por aseguradora' : 'By carrier',
      description: es
        ? 'Clientes con una póliza de la aseguradora indicada. Estado = el del CLIENTE; Estado de póliza = el de la póliza viva si la tiene, y si no la última que tuvo.'
        : 'Clients with a policy with the given carrier. Status = the CLIENT\'s; Policy status = that of the live policy if there is one, otherwise the most recent one.',
      icon: Building2,
      color: '#0369a1',
      params: [{ name: 'carrier', label: es ? 'Aseguradora' : 'Carrier', type: 'text', default: '' }],
    },
    {
      id: 'no-active-policy',
      title: es ? 'Sin póliza activa' : 'No active policy',
      description: es
        ? 'Clientes sin ninguna póliza activa. Buenos candidatos para reconectar.'
        : 'Clients without any active policy. Strong reconnect candidates.',
      icon: FileX2,
      color: '#ef4444',
      params: [],
    },
    {
      id: 'prospects',
      title: es ? 'Prospectos' : 'Prospects',
      description: es
        ? 'Clientes en estado PROSPECTO — oportunidades abiertas en tu pipeline.'
        : 'Clients in PROSPECT status — open pipeline opportunities.',
      icon: Sparkles,
      color: '#a855f7',
      params: [],
    },
  ];
}

function ageFromDOB(dob) {
  if (!dob) return '';
  // Parse from LOCAL Y/M/D so a UTC-midnight parse can't shift the birthday a
  // day, and compute age by calendar (the 365.25 division could be off by a
  // year over leap-heavy spans).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob).trim());
  const bd = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dob);
  if (isNaN(bd.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - bd.getFullYear();
  if (now.getMonth() < bd.getMonth() || (now.getMonth() === bd.getMonth() && now.getDate() < bd.getDate())) age--;
  return age;
}

// Inline export helpers — duplicated here so this view stays self-
// contained and works whether or not the parent injects window globals.
async function exportToExcel(data, filename) {
  if (!data.length) return;
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}
function exportToCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map((row) => headers.map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Label maps for the small enum columns. Mirror the labels in
// CustomReportsTab's filter builder so a value reads the same whether you
// filter by it or show it. Unknown values fall back to a humanized form so
// a legacy value (e.g. "undocumented") never renders blank.
function humanizeValue(v) {
  return String(v || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function maritalLabel(v, es) {
  const map = es
    ? { single: 'Soltero/a', married: 'Casado/a', widowed: 'Viudo/a', divorced: 'Divorciado/a', separated: 'Separado/a', domestic_partner: 'Unión de hecho' }
    : { single: 'Single', married: 'Married', widowed: 'Widowed', divorced: 'Divorced', separated: 'Separated', domestic_partner: 'Domestic partner' };
  return v ? (map[v] || humanizeValue(v)) : '';
}
// La lista vive en crm-app/estatusMigratorio.js y NO se copia aqui.
//
// Este mapa estaba escrito a mano y le faltaban `undocumented` y `asylum`, que
// el servidor si guarda. Sin entrada, caia a humanizeValue -- que solo quita
// guiones bajos -- y la descarga sacaba "Undocumented" donde el filtro dice
// "Indocumentado", y "Asylum" donde deberia decir "Asilado / Refugiado". Esa
// es la queja 11 de Lissette (2026-09-02): "el estatus migratorio sale
// cambiado". No daba ningun error; el informe se bajaba entero y con buena
// cara.
const immigrationLabel = (v, es) => etiquetaDeEstatus(v, es);
function languageLabel(v, es) {
  const map = es ? { en: 'Inglés', es: 'Español' } : { en: 'English', es: 'Spanish' };
  return v ? (map[v] || humanizeValue(v)) : '';
}

// Column rendering catalog — both the in-app table and the Excel/CSV
// export read from this single source so they stay in sync.
// Keys mirror the IDs that CustomReportsTab persists in CustomReport.columns.
function getResultColumns(lang) {
  const es = lang === 'es';
  return {
    name:        { label: es ? 'Cliente' : 'Client',         exportLabel: es ? 'Cliente' : 'Client',         get: (r) => `${r.lastName}, ${r.firstName}` },
    // Separate name parts — handy for mail merge / exports.
    firstName:   { label: es ? 'Nombre' : 'First name',       exportLabel: es ? 'Nombre' : 'First name',       get: (r) => r.firstName || '' },
    lastName:    { label: es ? 'Apellido' : 'Last name',      exportLabel: es ? 'Apellido' : 'Last name',      get: (r) => r.lastName || '' },
    email:       { label: 'Email',                            exportLabel: 'Email',                            get: (r) => r.email || '' },
    phone:       { label: es ? 'Teléfono' : 'Phone',          exportLabel: es ? 'Teléfono' : 'Phone',          get: (r) => r.phone || '' },
    phoneSecondary: { label: es ? 'Teléfono 2' : 'Phone 2',   exportLabel: es ? 'Teléfono 2' : 'Phone 2',      get: (r) => r.phoneSecondary || '' },
    cityState:   { label: es ? 'Ciudad / Estado' : 'City / State', exportLabel: es ? 'Ciudad / Estado' : 'City / State', get: (r) => [r.city, r.state].filter(Boolean).join(', ') },
    address:     { label: es ? 'Dirección' : 'Address',       exportLabel: es ? 'Dirección' : 'Address',       get: (r) => r.address || '' },
    city:        { label: es ? 'Ciudad' : 'City',             exportLabel: es ? 'Ciudad' : 'City',             get: (r) => r.city || '' },
    state:       { label: es ? 'Estado' : 'State',            exportLabel: es ? 'Estado' : 'State',            get: (r) => r.state || '' },
    county:      { label: es ? 'Condado' : 'County',          exportLabel: es ? 'Condado' : 'County',          get: (r) => r.county || '' },
    zipCode:     { label: es ? 'Código postal' : 'ZIP code',  exportLabel: es ? 'Código postal' : 'ZIP code',  get: (r) => r.zipCode || '' },
    status:      { label: 'Status',                           exportLabel: 'Status',                           get: (r) => r.status || '' },
    source:      { label: es ? 'Origen' : 'Source',           exportLabel: es ? 'Origen' : 'Source',           get: (r) => r.source || '' },
    tags:        { label: es ? 'Etiquetas del cliente' : 'Client tags', exportLabel: es ? 'Etiquetas del cliente' : 'Client tags', get: (r) => Array.isArray(r.tags) ? r.tags.join(', ') : '' },
    // Las de PÓLIZA son otras que las del cliente y hasta hoy no había forma de
    // sacarlas en un reporte (Lissette 2026-09-13). Salen de la MISMA póliza
    // que las demás columnas de póliza: la que hizo match con el filtro.
    policyTags:  { label: es ? 'Etiquetas de póliza' : 'Policy tags', exportLabel: es ? 'Etiquetas de póliza' : 'Policy tags', get: (r) => Array.isArray(r.policyTags) ? r.policyTags.join(', ') : '' },
    assignedAgent: { label: es ? 'Agente' : 'Agent', exportLabel: es ? 'Agente asignado' : 'Assigned agent', get: (r) => r.assignedAgent || '' },
    // ATIENDE / HANDLE (Lissette 2026-09-15). NO es el agente asignado: ese es
    // de quién es la póliza, y éste es quién lleva al cliente.
    //
    // Llega ya resuelto a NOMBRE desde el servidor (`conAtiende` en
    // utils/atiende.ts). Se resuelve allí y no aquí a propósito: el nombre de
    // un asiento lo pone la agencia en sus ajustes, y traducirlo en la pantalla
    // dejaría el Excel con ids crudos cada vez que la descarga saliera antes de
    // que cargara la lista del equipo.
    attendedBy:  { label: es ? 'Atiende' : 'Handled by', exportLabel: es ? 'Atiende (handle)' : 'Handled by', get: (r) => r.attendedBy || '' },
    language:    { label: es ? 'Idioma' : 'Language',         exportLabel: es ? 'Idioma' : 'Language',         get: (r) => languageLabel(r.preferredLanguage, es) },
    // H5: backend composes `dob` ("YYYY-MM-DD") from the encrypted-triple
    // columns. The legacy `dateOfBirth` plaintext column was dropped
    // 2026-05-20, so the report endpoint no longer returns it.
    age:         { label: es ? 'Edad' : 'Age',                exportLabel: es ? 'Edad' : 'Age',                get: (r) => ageFromDOB(r.dob) },
    dateOfBirth: { label: es ? 'Fecha de nacimiento' : 'Date of birth', exportLabel: es ? 'Fecha Nac.' : 'DOB', get: (r) => r.dob || '' },
    createdAt:   { label: es ? 'Creado' : 'Created',          exportLabel: es ? 'Creado' : 'Created',          get: (r) => r.createdAt ? fmt(r.createdAt) : '' },
    // ─ Household & family ──────────────────────────────────────────────
    maritalStatus:    { label: es ? 'Estado civil' : 'Marital status',     exportLabel: es ? 'Estado civil' : 'Marital status',     get: (r) => maritalLabel(r.maritalStatus, es) },
    smoker:          { label: es ? '¿Fuma?' : 'Smoker?', exportLabel: es ? '¿Fuma?' : 'Smoker?', get: (r) => (r.smoker === 'yes' ? (es ? 'Sí fuma' : 'Smoker') : r.smoker === 'no' ? (es ? 'No fuma' : 'Non-smoker') : '') },
    immigrationStatus:{ label: es ? 'Estatus migratorio' : 'Immigration',  exportLabel: es ? 'Estatus migratorio' : 'Immigration',  get: (r) => immigrationLabel(r.immigrationStatus, es) },
    // VENCIMIENTO DEL DOCUMENTO MIGRATORIO (Lissette 2026-09-13). Sale del
    // bloque cifrado de documentos, descifrado en el servidor: aquí llega ya
    // como "YYYY-MM-DD" y solo la FECHA — ni el número de USCIS ni el de la
    // tarjeta viajan en un reporte masivo.
    immigrationDocExpiration: {
      label: es ? 'Vence el documento' : 'Document expires',
      exportLabel: es ? 'Vence el documento' : 'Document expires',
      get: (r) => fechaCortaUS(r.immigrationDocExpiration),
    },
    spouseImmigrationDocExpiration: {
      label: es ? 'Vence el doc. (cónyuge)' : 'Document expires (spouse)',
      exportLabel: es ? 'Vence el documento (cónyuge)' : 'Document expires (spouse)',
      get: (r) => fechaCortaUS(r.spouseImmigrationDocExpiration),
    },
    employer:         { label: es ? 'Empleador' : 'Employer',              exportLabel: es ? 'Empleador' : 'Employer',              get: (r) => r.employer || '' },
    annualIncome:     { label: es ? 'Ingresos anuales' : 'Annual income',  exportLabel: es ? 'Ingresos anuales' : 'Annual income',  get: (r) => r.annualIncome || '' },
    householdSize:    { label: es ? 'Tamaño del hogar' : 'Household size',  exportLabel: es ? 'Tamaño del hogar' : 'Household size',  get: (r) => (r.householdSize ?? '') === '' ? '' : String(r.householdSize) },
    insuredMembers:   { label: es ? 'Miembros asegurados' : 'Insured members', exportLabel: es ? 'Miembros asegurados' : 'Insured members', get: (r) => (r.insuredMembers ?? '') === '' ? '' : String(r.insuredMembers) },
    dependentsCount:  { label: es ? 'Dependientes' : 'Dependents',         exportLabel: es ? 'Dependientes' : 'Dependents',         get: (r) => (r.dependentsCount ?? '') === '' ? '' : String(r.dependentsCount) },
    dependentsImmigration: {
      label: es ? 'Migratorio (dependientes)' : 'Dependents immigration',
      exportLabel: es ? 'Migratorio (dependientes)' : 'Dependents immigration',
      // Backend sends [{ name, status }] per dependent. Show "Name (Status)"
      // per dependent, comma-separated, reusing the same label map as the
      // principal/spouse immigration columns.
      get: (r) => Array.isArray(r.dependentsImmigration)
        ? r.dependentsImmigration
            .map((d) => {
              const lab = immigrationLabel(d.status, es);
              return d.name ? (lab ? `${d.name} (${lab})` : d.name) : lab;
            })
            .filter(Boolean)
            .join(', ')
        : '',
    },
    // UNA COLUMNA POR DEPENDIENTE, Y OTRA PARA SU ESTATUS.
    //
    // Lissette 2026-09-02, queja 4: "cuando pedimos dependiente y estatus
    // migratorio no coloca todo en una sola columna, se puede separar
    // dependiente tal y su estatus, dependiente tal y su estatus por columna?".
    //
    // La columna de arriba junta a toda la familia en UNA celda ("Ana
    // (Residente), Luis (Permiso de trabajo)"), que se lee pero no se puede
    // filtrar, ni ordenar, ni contar en una hoja de calculo. Estas la parten.
    //
    // SE QUEDA TAMBIEN LA JUNTA, a proposito: los informes ya guardados la
    // tienen elegida, y quitarla les cambiaria el resultado sin avisar. Quien
    // quiera la compacta la sigue teniendo.
    //
    // SEIS es el tope, y es una decision, no un limite tecnico. El informe
    // tiene un numero FIJO de columnas -- no puede crecer segun el cliente que
    // toque -- asi que hay que elegir uno. Con seis cabe una familia grande;
    // por encima de eso, la columna junta de arriba los sigue enseñando a
    // todos, asi que nadie pierde informacion, solo la comodidad de la rejilla.
    // Si algun dia hace falta subirlo, hay que subir con el
    // TOPE_COLUMNAS_DESDE_INFORME del servidor: la prueba
    // hojaDesdeInformeCabenLasColumnas los tiene atados.
    // Las claves y las etiquetas se declaran en crm-app/columnasDeDependientes.js
    // y las leen TAMBIEN el selector de columnas (CustomReportsTab). Estaban
    // escritas dos veces y se separaron: el renderizador tenia las doce y el
    // selector no ofrecia ninguna, asi que existian y no habia forma de
    // pedirlas (Lissette 2026-09-02: "sigo viendo los dependientes y sus
    // estatus en una misma columna"). Una sola lista, y ya no pueden divergir.
    ...Object.fromEntries(COLUMNAS_DE_DEPENDIENTES.flatMap(({ i, claveNombre, claveEstatus }) => [
      [claveNombre, {
        label: etiquetaDeDependiente(i, es),
        exportLabel: etiquetaDeDependiente(i, es),
        get: (r) => (Array.isArray(r.dependentsImmigration) ? (r.dependentsImmigration[i]?.name || '') : ''),
      }],
      [claveEstatus, {
        label: etiquetaDeEstatusDeDependiente(i, es),
        exportLabel: etiquetaDeEstatusDeDependiente(i, es),
        get: (r) => (Array.isArray(r.dependentsImmigration)
          ? immigrationLabel(r.dependentsImmigration[i]?.status, es)
          : ''),
      }],
    ])),
    // ─ Referral ────────────────────────────────────────────────────────
    referredBy:       { label: es ? 'Referido por' : 'Referred by',        exportLabel: es ? 'Referido por' : 'Referred by',        get: (r) => [r.referredByFirstName, r.referredByLastName].filter(Boolean).join(' ') },
    referredByPhone:  { label: es ? 'Tel. del referidor' : 'Referrer phone', exportLabel: es ? 'Tel. del referidor' : 'Referrer phone', get: (r) => r.referredByPhone || '' },
    // ─ Spouse ──────────────────────────────────────────────────────────
    spouseName:       { label: es ? 'Cónyuge' : 'Spouse',                  exportLabel: es ? 'Cónyuge' : 'Spouse',                  get: (r) => [r.spouseFirstName, r.spouseLastName].filter(Boolean).join(' ') },
    spouseEmployer:   { label: es ? 'Empleador (cónyuge)' : 'Spouse employer', exportLabel: es ? 'Empleador (cónyuge)' : 'Spouse employer', get: (r) => r.spouseEmployer || '' },
    spouseAnnualIncome:{ label: es ? 'Ingresos (cónyuge)' : 'Spouse income', exportLabel: es ? 'Ingresos (cónyuge)' : 'Spouse income', get: (r) => r.spouseAnnualIncome || '' },
    spouseImmigrationStatus:{ label: es ? 'Migratorio (cónyuge)' : 'Spouse immigration', exportLabel: es ? 'Migratorio (cónyuge)' : 'Spouse immigration', get: (r) => immigrationLabel(r.spouseImmigrationStatus, es) },
    // ─ Policy ──────────────────────────────────────────────────────────
    policyStatus:     { label: es ? 'Estado de póliza' : 'Policy status', exportLabel: es ? 'Estado póliza' : 'Policy status', get: (r) => (r.policyStatus || '').toString().toLowerCase() },
    policyType:       { label: es ? 'Tipo de póliza' : 'Policy type',     exportLabel: es ? 'Tipo póliza' : 'Policy type',     get: (r) => (r.policyType || '').toString().toLowerCase() },
    policyCarrier:    { label: es ? 'Aseguradora' : 'Carrier',            exportLabel: es ? 'Aseguradora' : 'Carrier',         get: (r) => r.policyCarrier || '' },
    policyPlanName:   { label: es ? 'Nombre del plan' : 'Plan name', exportLabel: es ? 'Nombre del plan' : 'Plan name', get: (r) => r.policyPlanName || '' },
    policyNumber:     { label: es ? 'Número de póliza' : 'Policy #', exportLabel: es ? 'Número póliza' : 'Policy #', get: (r) => r.policyNumber || '' },
    policyPremium:    { label: es ? 'Prima' : 'Premium', exportLabel: es ? 'Prima' : 'Premium', get: (r) => (r.policyPremium === '' || r.policyPremium == null) ? '' : String(r.policyPremium) },
    policyMemberId:   { label: 'Member ID', exportLabel: 'Member ID', get: (r) => r.policyMemberId || '' },
    policyApplicationNumber: { label: es ? 'Número de aplicación' : 'Application #', exportLabel: es ? 'Número de aplicación' : 'Application #', get: (r) => r.policyApplicationNumber || '' },
    policyEffective:  { label: es ? 'Fecha efectiva' : 'Effective date', exportLabel: es ? 'Fecha efectiva' : 'Effective date', get: (r) => r.policyEffective || '' },
    policyPaidStatus: { label: es ? 'Pago (este mes)' : 'Payment (this month)', exportLabel: es ? 'Pago mes' : 'Payment',
      get: (r) => ({ paid: es ? 'Pagó' : 'Paid', not_paid: es ? 'No pagó' : 'Not paid', grace: es ? 'Gracia' : 'Grace' }[r.policyPaidStatus] || '') },
    policyExpiration: { label: es ? 'Vence (póliza)' : 'Policy expires',  exportLabel: es ? 'Vence póliza' : 'Policy expires',  get: (r) => r.policyExpiration || '' },
    policyCancellationDate: { label: es ? 'Cancelada el' : 'Cancelled on', exportLabel: es ? 'Fecha de cancelacion' : 'Cancellation date', get: (r) => r.policyCancellationDate || '' },
    policyCancelReason: { label: es ? 'Motivo cancelación' : 'Cancel reason', exportLabel: es ? 'Motivo de cancelacion' : 'Cancellation reason', get: (r) => r.policyCancelReason || '' },
  };
}

// Default columns when the report doesn't specify (pre-built reports +
// legacy MVP saves). Keep in sync with DEFAULT_RESULT_COLUMNS in
// CustomReportsTab.jsx — they're the same set, just lives here to keep
// the two files importable without a cycle.
const DEFAULT_COLUMNS = ['name', 'email', 'phone', 'cityState', 'status', 'age', 'createdAt'];

function resolveColumns(requested, lang) {
  const catalog = getResultColumns(lang);
  const order = Object.keys(catalog);
  const keys = Array.isArray(requested) && requested.length ? requested : DEFAULT_COLUMNS;
  // Drop anything we don't know how to render, and dedupe.
  const filtered = [...new Set(keys.filter((k) => !!catalog[k]))];
  const resolved = filtered.length ? filtered : DEFAULT_COLUMNS;
  // Always render in the canonical catalog order (Client → Contact →
  // dates → Household → Referral → Spouse → Policy) regardless of the
  // order the user ticked the boxes. Keeps related columns together —
  // e.g. the principal's immigration status sits before the spouse's,
  // instead of wherever it happened to be checked.
  return [...resolved].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function rowsForExport(rows, lang, requestedColumns) {
  const catalog = getResultColumns(lang);
  const cols = resolveColumns(requestedColumns, lang);
  return rows.map((r) => {
    const out = {};
    for (const k of cols) {
      out[catalog[k].exportLabel] = catalog[k].get(r);
    }
    return out;
  });
}

export function ReportsClientsView({ lang = 'en', currentUser, onClientClick }) {
  const es = lang === 'es';
  const reports = useMemo(() => makeReportCatalog(lang), [lang]);
  // 'prebuilt' = the catalog of canned reports; 'custom' = the user's
  // own saved builder reports. We share one result detail pane for both,
  // so when a custom report runs we synthesize a minimal "active" report
  // and feed its rows into the same table below.
  const [tab, setTab] = useState('prebuilt');
  const [active, setActive] = useState(null); // { report, params } — runs when set
  const [paramValues, setParamValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  // Result sorting (Lissette 2026-06-22): click a column header to sort the
  // results A→Z / Z→A (and low→high / high→low for numbers — localeCompare with
  // numeric:true handles text and numbers). Clicking the same header toggles
  // the direction.
  // Se recalcula solo cuando cambian las filas: recorrer la lista entera en
  // cada pintada la congelaría con la cartera de una agencia grande.
  const totales = useMemo(() => (rows ? totalesDePersonas(rows) : null), [rows]);
  // Mandar el informe a Hojas (Lissette 2026-09-01: "sin tener que descargar y
  // volver a subirlo"). La hoja nace PRIVADA — lo decide el servidor.
  const [mandandoAHojas, setMandandoAHojas] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  // Qué se lleva el Excel/CSV. Un reporte PRE-ARMADO no tiene columnas
  // elegidas, así que sigue bajando TODO el catálogo ("cuando se exporte que
  // se bajen todos los datos", Lissette 2026-07-15). Un reporte propio cuyo
  // sentido es justamente elegir columnas baja ESAS columnas — bajarlo todo
  // hacía inútil el selector (Marilyn 2026-08-24: "le pido columnas
  // específicas y el excel se viene todo"). La casilla deja cambiarlo por
  // exportación, sin tocar el reporte guardado.
  const [exportAll, setExportAll] = useState(true);
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };
  const [error, setError] = useState(null);

  const openReport = (report) => {
    setActive(report);
    setRows(null);
    setMeta(null);
    setError(null);
    // Seed params with defaults so the user can hit Run without typing
    // anything for the common case.
    const defaults = {};
    report.params.forEach((p) => { defaults[p.name] = p.default; });
    setParamValues(defaults);
    if (report.params.length === 0) {
      // No params → auto-run on click for instant feedback.
      run(report, defaults);
    }
  };

  const closeReport = () => {
    setActive(null);
    setRows(null);
    setMeta(null);
    setError(null);
    setParamValues({});
  };

  const run = async (reportOverride, paramsOverride) => {
    const r = reportOverride || active;
    const p = paramsOverride || paramValues;
    if (!r) return;
    setBusy(true);
    setError(null);
    setRows(null);
    setMeta(null);
    try {
      const { rows: data, meta: m } = await runClientReport(r.id, p);
      setRows(data);
      setMeta(m);
      // Pre-armado: nadie eligió columnas, así que la exportación trae todo.
      setExportAll(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  // ── Detail view: one report + its params + result table ────────────────
  if (active) {
    const Icon = active.icon;
    const _d = new Date();
    const _today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
    const filenameBase = `deskte-${active.id}-${_today}`;
    // Las columnas que se llevan Excel y CSV: el catálogo entero, o las mismas
    // que pinta la tabla (resolveColumns aplica el mismo orden canónico, así
    // que el archivo sale con las columnas en el orden que se ven).
    const exportColumnKeys = exportAll
      ? Object.keys(getResultColumns(lang))
      : resolveColumns(meta?.columns, lang);

    // A HOJAS, sin pasar por el Excel. Se mandan EXACTAMENTE las mismas
    // columnas que se bajarian, para que no haya dos verdades sobre "que
    // lleva este informe".
    const mandarAHojas = async () => {
      if (!rows || rows.length === 0 || mandandoAHojas) return;
      setMandandoAHojas(true);
      try {
        const hoja = await crearHojaDesdeInforme({
          nombre: `${active.title} — ${_today}`,
          nombreDelInforme: active.title,
          filas: rowsForExport(rows, lang, exportColumnKeys),
        });
        toast.success(es
          ? `Hoja "${hoja.name}" creada con ${rows.length} filas. Es PRIVADA: solo la ves tú hasta que la compartas desde Hojas.`
          : `Sheet "${hoja.name}" created with ${rows.length} rows. It is PRIVATE: only you can see it until you share it from Sheets.`);
      } catch (e) {
        // Nunca en silencio: si esto fallara sin decir nada, la agente se iría
        // a Hojas a buscar algo que no está.
        toast.error(e?.message || (es ? 'No se pudo crear la hoja.' : 'Could not create the sheet.'));
      } finally {
        setMandandoAHojas(false);
      }
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1rem' }}>
          <button onClick={closeReport} style={{
            display: 'inline-flex', alignItems: 'center', gap: '.35rem',
            background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
            color: 'var(--c-text2,#7aa3c0)', fontSize: '.78rem', fontWeight: 600,
            padding: '.45rem .8rem', borderRadius: 8, cursor: 'pointer',
          }}>
            <ArrowLeft size={13} /> {es ? 'Volver' : 'Back'}
          </button>
        </div>
        <div style={{
          background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)',
          borderRadius: 14, padding: '1.25rem', marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '.5rem' }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: `${active.color}22`,
              border: `1px solid ${active.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={18} color={active.color} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
                {active.title}
              </div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>
                {active.description}
              </div>
            </div>
          </div>
          {active.params.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.6rem', alignItems: 'flex-end', marginTop: '1rem' }}>
              {active.params.map((p) => (
                <div key={p.name} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    {p.label}
                  </label>
                  {p.type === 'state' ? (
                    <select
                      value={paramValues[p.name] || ''}
                      onChange={(e) => setParamValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                      className="dkt-inp"
                      style={{ fontSize: '.85rem', padding: '.45rem .65rem', width: 200, cursor: 'pointer' }}
                    >
                      {US_STATES.map(([code, name]) => (
                        <option key={code} value={code}>{code} — {name}</option>
                      ))}
                    </select>
                  ) : p.type === 'policy-type' ? (
                    <select
                      value={paramValues[p.name] || ''}
                      onChange={(e) => setParamValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                      className="dkt-inp"
                      style={{ fontSize: '.85rem', padding: '.45rem .65rem', width: 240, cursor: 'pointer' }}
                    >
                      {POLICY_TYPES.map((pt) => (
                        <option key={pt.id} value={pt.id}>{pt.icon} {es ? (pt.labelEs || pt.label) : pt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={p.type === 'number' ? 'number' : 'text'}
                      value={paramValues[p.name] ?? ''}
                      onChange={(e) => setParamValues((prev) => ({ ...prev, [p.name]: p.type === 'number' ? Number(e.target.value) : e.target.value }))}
                      className="dkt-inp"
                      style={{ fontSize: '.85rem', padding: '.45rem .65rem', width: 160 }}
                    />
                  )}
                </div>
              ))}
              <button
                onClick={() => run()}
                disabled={busy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '.4rem',
                  background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none',
                  color: '#fff', fontSize: '.82rem', fontWeight: 700,
                  padding: '.5rem 1rem', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? (es ? 'Ejecutando…' : 'Running…') : (es ? '▶ Ejecutar' : '▶ Run')}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '.75rem 1rem', color: '#fca5a5', fontSize: '.85rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {busy && !rows && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--c-text3,#45688a)' }}>
            {es ? 'Ejecutando reporte…' : 'Running report…'}
          </div>
        )}

        {rows && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.75rem', flexWrap: 'wrap', gap: '.5rem' }}>
              <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)' }}>
                <strong style={{ color: 'var(--c-cyan,#00d4ff)' }}>{rows.length}</strong> {es ? 'resultados' : 'results'}
                {meta?.generatedAt && (
                  <span style={{ marginLeft: '.65rem', color: 'var(--c-text3,#45688a)', fontSize: '.72rem' }}>
                    · {new Date(meta.generatedAt).toLocaleString('en-US')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Qué columnas se bajan. Marcada = TODO el catálogo (dirección,
                    ZIP, ingresos, hogar, dependientes, cónyuge, póliza…);
                    desmarcada = exactamente las columnas que muestra la tabla.
                    Arranca marcada en los reportes pre-armados y desmarcada en
                    los propios que sí eligieron columnas — ver `exportAll`. */}
                <label
                  title={es
                    ? 'Marcada baja todas las columnas del catálogo; desmarcada baja solo las columnas que ves en la tabla.'
                    : 'Checked downloads every catalog column; unchecked downloads only the columns shown in the table.'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', userSelect: 'none' }}
                >
                  <input
                    type="checkbox"
                    checked={exportAll}
                    onChange={(e) => setExportAll(e.target.checked)}
                    style={{ accentColor: '#00d4ff', width: 14, height: 14, cursor: 'pointer' }}
                  />
                  {es ? 'Bajar todas las columnas' : 'Download all columns'}
                </label>
                <button onClick={() => exportToExcel(rowsForExport(rows, lang, exportColumnKeys), `${filenameBase}.xlsx`)} disabled={rows.length === 0} className="dkt-btn-g">
                  <Download size={13} /> Excel
                </button>
                <button onClick={() => exportToCSV(rowsForExport(rows, lang, exportColumnKeys), `${filenameBase}.csv`)} disabled={rows.length === 0} className="dkt-btn-g">
                  <Download size={13} /> CSV
                </button>
                <button onClick={mandarAHojas} disabled={rows.length === 0 || mandandoAHojas} className="dkt-btn-g"
                  title={es
                    ? 'Crea una hoja con este resultado. Nace privada: solo la ves tú hasta que la compartas.'
                    : 'Creates a sheet with this result. It starts private: only you can see it until you share it.'}>
                  <LayoutGrid size={13} /> {mandandoAHojas ? (es ? 'Creando…' : 'Creating…') : (es ? 'A Hojas' : 'To Sheets')}
                </button>
              </div>
            </div>

            {/* CUÁNTAS PERSONAS, no cuántos hogares. "340 resultados" puede
                ser 900 personas: cada fila lleva al titular, su cónyuge y sus
                dependientes. Lo que se vende y se renueva es por persona. */}
            {/* SE DEJÓ DE BUSCAR ANTES DE LLEGAR AL FINAL. Solo pasa con un
                filtro que el servidor tiene que resolver descifrando (hoy, el
                vencimiento del documento) y una cartera enorme. Callarlo haría
                que la lista se leyera como completa — que es exactamente lo
                que la regla "no mostrar listas capadas" prohíbe. */}
            {meta?.alTope && (
              <div style={{
                marginBottom: '.75rem', padding: '.5rem .7rem', borderRadius: 8,
                background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.3)',
                color: 'var(--c-amber,#ffb700)', fontSize: '.75rem', lineHeight: 1.45,
              }}>
                {es
                  ? `Se revisaron los primeros ${meta.revisados} clientes y puede haber más que cumplan. Añade otra condición (por ejemplo el estatus migratorio) para acotar la búsqueda.`
                  : `Only the first ${meta.revisados} clients were scanned and there may be more matches. Add another condition (e.g. immigration status) to narrow the search.`}
              </div>
            )}
            {totales && rows.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.85rem' }}>
                {[
                  { k: 'titulares', n: totales.titulares, label: es ? 'Titulares' : 'Primary' },
                  {
                    k: 'conyuges', n: totales.conyuges, label: es ? 'Cónyuges' : 'Spouses',
                    ayuda: es
                      ? 'Los que están cargados en la ficha. Una ficha casada sin cónyuge cargado no suma.'
                      : 'Those recorded on the file. A married file with no spouse on it does not count.',
                  },
                  { k: 'dependientes', n: totales.dependientes, label: es ? 'Dependientes' : 'Dependents' },
                  { k: 'personas', n: totales.personas, label: es ? 'Personas en total' : 'People in total', fuerte: true },
                ].map((t) => (
                  <div key={t.k} title={t.ayuda || undefined}
                    style={{
                      flex: '1 1 8.5rem', minWidth: '8.5rem',
                      background: t.fuerte ? 'var(--c-cyan-dim,rgba(0,212,255,.1))' : 'var(--c-surface,#0d1c2e)',
                      border: `1px solid ${t.fuerte ? 'rgba(0,212,255,.35)' : 'var(--c-border,#1a3550)'}`,
                      borderRadius: 10, padding: '.55rem .75rem',
                      cursor: t.ayuda ? 'help' : 'default',
                    }}>
                    <div style={{
                      fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.15,
                      // Los dígitos de columnas que se comparan van a ancho fijo:
                      // si no, 111 se ve más corto que 999 y engaña al ojo.
                      fontVariantNumeric: 'tabular-nums',
                      color: t.fuerte ? 'var(--c-cyan,#00d4ff)' : 'var(--c-white,#eef6ff)',
                    }}>{t.n.toLocaleString('en-US')}</div>
                    <div style={{
                      fontSize: '.62rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                      fontFamily: 'var(--font-mono)', color: 'var(--c-text3,#45688a)', marginTop: 2,
                    }}>{t.label}</div>
                  </div>
                ))}
              </div>
            )}

            {rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--c-text3,#45688a)', background: 'var(--c-surface,#0d1c2e)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 12 }}>
                {es ? 'Sin resultados para los criterios elegidos.' : 'No results for the chosen criteria.'}
              </div>
            ) : (() => {
              // Resolve which columns to render at the table-render time
              // so changes to the active report propagate without a full
              // remount of the detail view.
              const catalog = getResultColumns(lang);
              const activeKeys = resolveColumns(meta?.columns, lang);
              // Sort the displayed rows by the clicked column. localeCompare with
              // numeric:true gives alphabetical order for text AND low→high order
              // for numbers (premium, age, application #), in one comparator.
              const sortedRows = (sortKey && catalog[sortKey])
                ? [...rows].sort((a, b) => {
                    const av = String(catalog[sortKey].get(a) ?? '');
                    const bv = String(catalog[sortKey].get(b) ?? '');
                    const cmp = av.localeCompare(bv, es ? 'es' : 'en', { numeric: true, sensitivity: 'base' });
                    return sortDir === 'asc' ? cmp : -cmp;
                  })
                : rows;
              return (
              <>
              {/* Chart over the SAME rows the table shows — no second query and
                  no second filter, so the two can never disagree. */}
              <GroupedChart
                rows={sortedRows}
                lang={lang}
                dimensions={CLIENT_DIMENSIONS(es)}
                color="#a855f7"
              />
              <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                        {activeKeys.map((k) => (
                          <th key={k} onClick={() => toggleSort(k)} title={es ? 'Clic para ordenar' : 'Click to sort'} style={{ textAlign: 'left', padding: '.5rem .65rem', color: sortKey === k ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--c-border,#1a3550)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                            {catalog[k].label}{sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((r) => {
                        // Whole row opens the client's profile (ficha). Each
                        // report row carries the client id, so we hand it to
                        // the shell which switches to the Clients view and
                        // opens that client's detail.
                        const clickable = !!(onClientClick && r.id);
                        return (
                        <tr
                          key={r.id}
                          onClick={clickable ? () => onClientClick(r.id) : undefined}
                          title={clickable ? (es ? 'Abrir ficha del cliente' : 'Open client profile') : undefined}
                          style={{ borderBottom: '1px solid rgba(26,53,80,.4)', cursor: clickable ? 'pointer' : 'default' }}
                          onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = 'rgba(0,212,255,.06)'; } : undefined}
                          onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
                        >
                          {activeKeys.map((k) => {
                            // Status cell gets a colored chip; every other
                            // cell uses the catalog `get()` raw output.
                            if (k === 'status') {
                              return (
                                <td key={k} style={{ padding: '.5rem .65rem' }}>
                                  <span style={{ fontSize: '.7rem', fontWeight: 700, color: r.status === 'ACTIVE' ? '#00ff88' : '#7aa3c0', textTransform: 'capitalize' }}>
                                    {(r.status || '').toLowerCase()}
                                  </span>
                                </td>
                              );
                            }
                            const cellClass = k === 'name'
                              ? { color: 'var(--c-white,#eef6ff)', fontWeight: 600 }
                              : k === 'phone' || k === 'age' || k === 'dateOfBirth' || k === 'createdAt'
                                ? { color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)' }
                                : { color: 'var(--c-text2,#7aa3c0)' };
                            const value = catalog[k].get(r);
                            return (
                              <td key={k} style={{ padding: '.5rem .65rem', ...cellClass }}>
                                {value || (k === 'name' ? '' : '—')}
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </>
              );
            })()}
          </div>
        )}
      </div>
    );
  }

  // When a custom report runs, surface its results in the same detail
  // pane the pre-built reports use. We synthesize a minimal "report"
  // object so the existing rendering keeps working unchanged.
  const handleCustomRun = (report, runRows, runMeta) => {
    setActive({
      id: `custom:${report.id}`,
      title: report.name,
      description: report.description || '',
      icon: Wrench,
      color: '#a855f7',
      params: [],
    });
    setRows(runRows);
    setMeta(runMeta);
    setExportAll(!runMeta?.columnsPicked);
    setBusy(false);
    setError(null);
  };

  // ── Index view: tabs + grid of report cards ───────────────────────────
  const tabButton = (id, label, Icon) => (
    <button
      key={id}
      onClick={() => setTab(id)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '.4rem',
        background: tab === id ? 'rgba(0,212,255,.12)' : 'transparent',
        border: tab === id ? '1px solid rgba(0,212,255,.4)' : '1px solid transparent',
        color: tab === id ? '#00d4ff' : 'var(--c-text2,#7aa3c0)',
        fontSize: '.82rem', fontWeight: 700,
        padding: '.5rem .9rem', borderRadius: 8, cursor: 'pointer',
        transition: 'all .15s',
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div className="dkt-sec-title">{es ? 'Reportes' : 'Reports'}</div>
        <div className="dkt-sec-sub">
          {tab === 'prebuilt'
            ? (es
                ? 'Consultas pre-armadas. Cada ejecución queda registrada en la bitácora de auditoría.'
                : 'Pre-built queries. Each run is recorded in the audit log.')
            : (es
                ? 'Tus reportes personalizados. Combiná filtros, guardalos y reusalos.'
                : 'Your custom reports. Combine filters, save them, reuse them.')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--c-border,#1a3550)', paddingBottom: '.5rem' }}>
        {tabButton('prebuilt', es ? 'Pre-armados' : 'Pre-built', LayoutGrid)}
        {tabButton('custom', es ? 'Mis reportes' : 'My reports', Wrench)}
      </div>

      {tab === 'prebuilt' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '.85rem' }}>
          {reports.map((r) => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                onClick={() => openReport(r)}
                style={{
                  textAlign: 'left',
                  background: 'var(--c-surface,#0d1c2e)',
                  border: '1px solid var(--c-border,#1a3550)',
                  borderRadius: 12, padding: '1rem',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', gap: '.5rem',
                  transition: 'all .15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = r.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: `${r.color}22`, border: `1px solid ${r.color}55`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={17} color={r.color} />
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '.95rem', color: 'var(--c-white,#eef6ff)' }}>
                  {r.title}
                </div>
                <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.45 }}>
                  {r.description}
                </div>
                {r.params.length > 0 && (
                  <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>
                    {r.params.map((p) => `${p.label}: ${p.default}`).join(' · ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <CustomReportsTab lang={lang} currentUser={currentUser} onRun={handleCustomRun} />
      )}
    </div>
  );
}
